import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUpload, FaSpinner, FaTrash, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Parse from 'parse';
import { useTranslation } from 'react-i18next';
import { agencyService } from '../../services/agencyService';
import { authService } from '../../services/ParseService';

const RegisterAgencyForm = ({ onSuccessfulSubmission }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    agencyName: '',
    description: '',
    contactEmail: '',
    paymentMethod: '',
    documents: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('idle'); // idle, submitting, submitted
  const [documentPreview, setDocumentPreview] = useState([]);
  const [documentError, setDocumentError] = useState('');

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        agencyName: currentUser.get('name') || currentUser.get('username') || '',
        contactEmail: currentUser.get('email') || ''
      }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      setDocumentError(t('agency.register.errors.maxDocuments'));
      return;
    }
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    if (validFiles.length !== files.length) {
      setDocumentError(t('agency.register.errors.invalidDocuments'));
    } else {
      setDocumentError('');
    }

    // Create preview URLs
    const previews = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type
    }));

    setFormData(prev => ({
      ...prev,
      documents: validFiles
    }));
    setDocumentPreview(previews);
  };

  const removeDocument = (indexToRemove) => {
    const updatedPreviews = documentPreview.filter((_, index) => index !== indexToRemove);
    const updatedFiles = formData.documents.filter((_, index) => index !== indexToRemove);

    setDocumentPreview(updatedPreviews);
    setFormData(prev => ({
      ...prev,
      documents: updatedFiles
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus('submitting');

    // Validate document upload
    if (!formData.documents || formData.documents.length === 0) {
      setDocumentError(t('agency.register.errors.documentUpload'));
      setIsSubmitting(false);
      return;
    }

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error(t('agency.register.errors.notLoggedIn'));
      }

      // Convert files to Parse.File objects
      const parseFiles = await Promise.all(
        formData.documents.map(async (file) => {
          const parseFile = new Parse.File(file.name, file);
          await parseFile.save();
          return parseFile;
        })
      );

      // Submit agency registration
      const result = await agencyService.submitAgencyRegistration({
        user: currentUser,
        agencyName: formData.agencyName,
        agencyDescription: formData.description,
        contactEmail: formData.contactEmail,
        paymentMethod: formData.paymentMethod,
        documents: parseFiles
      });

      toast.success(t('agency.register.submissionStatus.submitted'));
      setSubmissionStatus('submitted');
      if (onSuccessfulSubmission) {
        onSuccessfulSubmission(result);
      }
    } catch (error) {
      console.error('Agency registration full error:', error);
      
      // More detailed error handling
      if (error.code) {
        switch (error.code) {
          case Parse.Error.CONNECTION_FAILED:
            toast.error(t('agency.register.errors.networkError'));
            break;
          case Parse.Error.INVALID_SESSION_TOKEN:
            toast.error(t('agency.register.errors.sessionExpired'));
            break;
          default:
            toast.error(t('agency.register.errors.unknownError'));
        }
      } else {
        toast.error(t('agency.register.errors.unknownError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render submission review screen
  if (submissionStatus === 'submitted') {
    return (
      <Container>
        <SubmissionReviewContainer>
          <FaCheckCircle style={{ color: '#4CAF50', fontSize: '5rem', marginBottom: '1rem' }} />
          <h2>{t('agency.register.submissionStatus.submitted')}</h2>
          <p>{t('agency.register.submissionStatus.reviewMessage')}</p>
          <ReviewDetails>
            <h3>{t('agency.register.submissionStatus.applicationDetails')}:</h3>
            <ul>
              <li><strong>{t('agency.register.formFields.agencyName')}:</strong> {formData.agencyName}</li>
              <li><strong>{t('agency.register.formFields.contactEmail')}:</strong> {formData.contactEmail}</li>
              <li><strong>{t('agency.register.formFields.documents')}:</strong> {formData.documents.length}</li>
            </ul>
          </ReviewDetails>
          <p>{t('agency.register.submissionStatus.reviewMessage')}</p>
        </SubmissionReviewContainer>
      </Container>
    );
  }

  // Render submission form
  return (
    <Container>
      <h1>{t('agency.register.title')}</h1>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="agencyName">{t('agency.register.formFields.agencyName')} *</Label>
          <Input
            type="text"
            id="agencyName"
            name="agencyName"
            value={formData.agencyName}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="description">{t('agency.register.formFields.description')}</Label>
          <TextArea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t('agency.register.formFields.descriptionPlaceholder')}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="contactEmail">{t('agency.register.formFields.contactEmail')} *</Label>
          <Input
            type="email"
            id="contactEmail"
            name="contactEmail"
            value={formData.contactEmail}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="paymentMethod">{t('agency.register.formFields.paymentMethod')} *</Label>
          <Select
            id="paymentMethod"
            name="paymentMethod"
            value={formData.paymentMethod}
            onChange={handleInputChange}
            required
          >
            <option value="">{t('agency.register.formFields.paymentMethod')}</option>
            <option value="bank_transfer">{t('agency.register.formFields.bankTransfer')}</option>
            <option value="paypal">{t('agency.register.formFields.paypal')}</option>
            <option value="stripe">{t('agency.register.formFields.stripe')}</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>{t('agency.register.formFields.documents')} *</Label>
          <FileUploadContainer>
            <FileInput 
              type="file" 
              id="documents" 
              multiple 
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange} 
            />
            <FileInputLabel htmlFor="documents">
              <FaUpload /> {t('agency.register.formFields.documents')}
            </FileInputLabel>
            {documentError && <ErrorMessage>{documentError}</ErrorMessage>}
            
            {documentPreview.length > 0 && (
              <DocumentPreviewContainer>
                {documentPreview.map((doc, index) => (
                  <DocumentPreviewItem key={index}>
                    {doc.type.includes('image') ? (
                      <img src={doc.preview} alt={t('agency.register.documentPreview.image')} />
                    ) : (
                      <PDFPlaceholder>{t('agency.register.documentPreview.pdf')}</PDFPlaceholder>
                    )}
                    <RemoveButton onClick={() => removeDocument(index)}>
                      <FaTrash /> 
                    </RemoveButton>
                  </DocumentPreviewItem>
                ))}
              </DocumentPreviewContainer>
            )}
          </FileUploadContainer>
        </FormGroup>

        <SubmitButton 
          type="submit" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <FaSpinner /> {t('agency.register.submissionStatus.submitting')}
            </>
          ) : (
            t('agency.register.submissionStatus.idle')
          )}
        </SubmitButton>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h1 {
    color: #1a2a6c;
    margin-bottom: 2rem;
    font-size: 1.8rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #1a2a6c;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #1a2a6c;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #1a2a6c;
  }
`;

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  background-color: #1a2a6c;
  color: white;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 200px;
  align-self: center;
`;

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
  margin-top: 10px;
`;

const DocumentPreviewContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const DocumentPreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PDFPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  color: #666;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(255, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  padding: 1rem;
  background: #1a2a6c;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #2a3a7c;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SubmissionReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
`;

const ReviewDetails = styled.div`
  background-color: #f4f4f4;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  width: 100%;

  ul {
    list-style-type: none;
    padding: 0;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const StatusMessage = styled.div`
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 1.5rem;
  font-weight: 500;

  ${props => {
    switch (props.$status) {
      case 'in_review':
        return 'background: #fff3cd; color: #856404;';
      case 'approved':
        return 'background: #d4edda; color: #155724;';
      case 'rejected':
        return 'background: #f8d7da; color: #721c24;';
      default:
        return '';
    }
  }}
`;

export default RegisterAgencyForm;
